/**
 * Animations Module
 * Modern GSAP animations for home and expertise pages
 * 
 * @package JD_Theme
 */

export function initAnimations() {
  // Check if GSAP is available
  if (typeof gsap === 'undefined' || !window.gsap) {
    console.warn('GSAP is not loaded');
    return;
  }
  
  // Check if ScrollTrigger is available (it's attached to gsap object when loaded from CDN)
  if (!window.ScrollTrigger) {
    console.warn('ScrollTrigger is not loaded');
    return;
  }
  
  // Register ScrollTrigger plugin
  gsap.registerPlugin(window.ScrollTrigger);

  // Hero section animations
  animateHero();
  
  // Expertise cards animations
  animateExpertiseCards();
  
  // Events carousel animations
  animateEventsCards();
  
  // Icons section animations
  animateIconsSection();
  
  // Products grid animations
  animateProductsGrid();
  
  // Contact block animation
  animateContactBlock();
  
  // Ebay block animation
  animateEbayBlock();
  
  // Section titles animation
  animateSectionTitles();
}

// Hero section fade in and slide up
function animateHero() {
  const hero = document.querySelector('.hero-section, .hero-simple');
  if (!hero) return;
  
  const heroContent = hero.querySelector('.hero-content, .hero-simple__content');
  if (!heroContent) return;
  
  // Ensure all hero elements are visible first
  gsap.set(heroContent.children, { opacity: 1, y: 0 });
  
  gsap.from(heroContent.children, {
    opacity: 0,
    y: 30,
    duration: 0.8,
    stagger: 0.2,
    ease: 'power3.out',
    delay: 0.2
  });
}

// Expertise cards staggered animation
function animateExpertiseCards() {
  const cards = document.querySelectorAll('.expertise-card');
  if (cards.length === 0) return;
  
  // Ensure cards are visible first
  gsap.set(cards, { opacity: 1, y: 0, scale: 1 });
  
  if (!window.ScrollTrigger) {
    // Fallback animation without ScrollTrigger
    gsap.from(cards, {
      opacity: 0,
      y: 50,
      scale: 0.95,
      duration: 0.6,
      stagger: 0.15,
      ease: 'power2.out',
      delay: 0.3
    });
    return;
  }
  
  gsap.from(cards, {
    scrollTrigger: {
      trigger: '.expertise-grid',
      start: 'top 80%',
      toggleActions: 'play none none none'
    },
    opacity: 0,
    y: 50,
    scale: 0.95,
    duration: 0.6,
    stagger: 0.15,
    ease: 'power2.out'
  });
}

// Events carousel cards animation
function animateEventsCards() {
  const eventCards = document.querySelectorAll('.event-card');
  if (eventCards.length === 0) return;
  
  // Ensure cards are visible
  gsap.set(eventCards, { opacity: 1, x: 0 });
  
  if (!window.ScrollTrigger) return;
  
  gsap.from(eventCards, {
    scrollTrigger: {
      trigger: '.events-carousel',
      start: 'top 80%',
      toggleActions: 'play none none none'
    },
    opacity: 0,
    x: -30,
    duration: 0.5,
    stagger: 0.1,
    ease: 'power2.out'
  });
}

// Icons section animation (expertise detail page)
function animateIconsSection() {
  const iconItems = document.querySelectorAll('.expertise-icon-item');
  if (iconItems.length === 0) return;
  
  if (!window.ScrollTrigger) return;
  
  iconItems.forEach((item, index) => {
    const icon = item.querySelector('.expertise-icon-item__icon');
    const content = item.querySelector('.expertise-icon-item__content');
    
    if (icon) {
      gsap.from(icon, {
        scrollTrigger: {
          trigger: item,
          start: 'top 85%',
          toggleActions: 'play none none none'
        },
        opacity: 0,
        scale: 0,
        rotation: -180,
        duration: 0.6,
        ease: 'back.out(1.7)',
        delay: index * 0.1
      });
    }
    
    if (content) {
      gsap.from(content.children, {
        scrollTrigger: {
          trigger: item,
          start: 'top 85%',
          toggleActions: 'play none none none'
        },
        opacity: 0,
        x: -20,
        duration: 0.5,
        stagger: 0.1,
        ease: 'power2.out',
        delay: 0.3 + (index * 0.1)
      });
    }
  });
}

// Products grid animation
function animateProductsGrid() {
  const productCards = document.querySelectorAll('.expertise-product-card');
  if (productCards.length === 0) return;
  
  // Ensure cards are visible
  gsap.set(productCards, { opacity: 1, y: 0, scale: 1 });
  
  if (!window.ScrollTrigger) return;
  
  gsap.from(productCards, {
    scrollTrigger: {
      trigger: '.expertise-products-grid',
      start: 'top 80%',
      toggleActions: 'play none none none'
    },
    opacity: 0,
    y: 40,
    scale: 0.9,
    duration: 0.6,
    stagger: {
      amount: 0.8,
      from: 'start',
      ease: 'power1.inOut'
    },
    ease: 'power3.out'
  });
}

// Contact block animation
function animateContactBlock() {
  const contactBlock = document.querySelector('.contact-block');
  if (!contactBlock) return;
  
  const title = contactBlock.querySelector('.contact-block__title');
  const text = contactBlock.querySelector('.contact-block__text');
  const button = contactBlock.querySelector('.contact-block__button');
  
  const elements = [title, text, button].filter(el => el);
  
  // Ensure elements are visible
  gsap.set(elements, { opacity: 1, y: 0 });
  
  if (!window.ScrollTrigger) return;
  
  if (elements.length > 0) {
    gsap.from(elements, {
      scrollTrigger: {
        trigger: contactBlock,
        start: 'top 80%',
        toggleActions: 'play none none none'
      },
      opacity: 0,
      y: 30,
      duration: 0.7,
      stagger: 0.15,
      ease: 'power2.out'
    });
  }
}

// Ebay block animation
function animateEbayBlock() {
  const ebayBlock = document.querySelector('.ebay-block');
  if (!ebayBlock) return;
  
  const content = ebayBlock.querySelector('.ebay-block__content');
  if (!content) return;
  
  // Ensure content is visible
  gsap.set(content.children, { opacity: 1, y: 0 });
  
  if (!window.ScrollTrigger) return;
  
  gsap.from(content.children, {
    scrollTrigger: {
      trigger: ebayBlock,
      start: 'top 80%',
      toggleActions: 'play none none none'
    },
    opacity: 0,
    y: 25,
    duration: 0.6,
    stagger: 0.15,
    ease: 'power2.out'
  });
}

// Section titles animation
function animateSectionTitles() {
  const sectionTitles = document.querySelectorAll('.section-title');
  if (sectionTitles.length === 0) return;
  
  // Ensure titles are visible
  gsap.set(sectionTitles, { opacity: 1, x: 0 });
  
  if (!window.ScrollTrigger) return;
  
  sectionTitles.forEach(title => {
    gsap.from(title, {
      scrollTrigger: {
        trigger: title,
        start: 'top 85%',
        toggleActions: 'play none none none'
      },
      opacity: 0,
      x: -30,
      duration: 0.7,
      ease: 'power2.out'
    });
  });
}
